// src/pages/finance-portal/journal-entries/JournalEntryForm.js

import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert,
    CircularProgress, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const emptyLine = { account_id: '', debit_amount: '', credit_amount: '', memo: '' };

export default function JournalEntryForm({ open, onClose, onSaved, editEntry }) {
    const [accounts, setAccounts] = useState([]);
    const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState('');
    const [reference, setReference] = useState('');
    const [lines, setLines] = useState([{ ...emptyLine }, { ...emptyLine }]);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            axiosInstance.get('/api/finance/coa', { params: { flat: true, status: 'ACTIVE' } })
                .then(res => {
                    if (res.data.success) {
                        setAccounts(res.data.data.filter(a => !a.is_header));
                    }
                }).catch(console.error);

            if (editEntry) {
                setEntryDate(editEntry.entry_date);
                setDescription(editEntry.description || '');
                setReference(editEntry.reference || '');
                setLines(editEntry.lines.map(l => ({
                    account_id: l.account_id,
                    debit_amount: l.debit_amount || '',
                    credit_amount: l.credit_amount || '',
                    memo: l.memo || '',
                })));
            } else {
                setEntryDate(new Date().toISOString().split('T')[0]);
                setDescription('');
                setReference('');
                setLines([{ ...emptyLine }, { ...emptyLine }]);
            }
            setError(null);
        }
    }, [open, editEntry]);

    const updateLine = (index, field, value) => {
        const updated = [...lines];
        updated[index] = { ...updated[index], [field]: value };
        // If entering debit, clear credit and vice versa
        if (field === 'debit_amount' && value) updated[index].credit_amount = '';
        if (field === 'credit_amount' && value) updated[index].debit_amount = '';
        setLines(updated);
    };

    const addLine = () => setLines([...lines, { ...emptyLine }]);
    const removeLine = (index) => { if (lines.length > 2) setLines(lines.filter((_, i) => i !== index)); };

    const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit_amount) || 0), 0);
    const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit_amount) || 0), 0);
    const difference = Math.abs(totalDebit - totalCredit);
    const isBalanced = difference < 0.01;

    const handleSave = async (postImmediately = false) => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                entry_date: entryDate,
                description,
                reference,
                status: postImmediately ? 'POSTED' : 'DRAFT',
                lines: lines.filter(l => l.account_id).map(l => ({
                    account_id: l.account_id,
                    debit_amount: parseFloat(l.debit_amount) || 0,
                    credit_amount: parseFloat(l.credit_amount) || 0,
                    memo: l.memo,
                })),
            };

            if (editEntry) {
                await axiosInstance.put(`/api/finance/journal-entries/${editEntry.id}`, payload);
            } else {
                await axiosInstance.post('/api/finance/journal-entries', payload);
            }
            onSaved?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save journal entry');
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? '0.00' : num.toFixed(2);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                {editEntry ? `Edit Journal Entry ${editEntry.entry_number}` : 'New Journal Entry'}
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '10px', fontFamily }}>{error}</Alert>}

                {/* Header Fields */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                        label="Date" type="date" size="small" required
                        value={entryDate} onChange={e => setEntryDate(e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 160 }}
                    />
                    <TextField
                        label="Description" size="small" fullWidth
                        value={description} onChange={e => setDescription(e.target.value)}
                        InputProps={{ sx: { fontFamily } }} InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField
                        label="Reference" size="small"
                        value={reference} onChange={e => setReference(e.target.value)}
                        InputProps={{ sx: { fontFamily } }} InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 140 }}
                    />
                </Box>

                {/* Lines Header */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 1 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: '#64748b', flex: 2, textTransform: 'uppercase' }}>Account</Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: '#64748b', width: 120, textTransform: 'uppercase' }}>Debit</Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: '#64748b', width: 120, textTransform: 'uppercase' }}>Credit</Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: '#64748b', flex: 1, textTransform: 'uppercase' }}>Memo</Typography>
                    <Box sx={{ width: 36 }} />
                </Box>

                {/* Lines */}
                {lines.map((line, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <Autocomplete
                            size="small"
                            options={accounts}
                            getOptionLabel={a => a ? `${a.account_number} - ${a.name}` : ''}
                            value={accounts.find(a => a.id === line.account_id) || null}
                            onChange={(_, val) => updateLine(index, 'account_id', val?.id || '')}
                            renderInput={(params) => <TextField {...params} placeholder="Select account" InputProps={{ ...params.InputProps, sx: { fontFamily, fontSize: '0.8rem' } }} />}
                            sx={{ flex: 2 }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                        <TextField
                            size="small" type="number" placeholder="0.00"
                            value={line.debit_amount} onChange={e => updateLine(index, 'debit_amount', e.target.value)}
                            InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                            sx={{ width: 120 }}
                        />
                        <TextField
                            size="small" type="number" placeholder="0.00"
                            value={line.credit_amount} onChange={e => updateLine(index, 'credit_amount', e.target.value)}
                            InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                            sx={{ width: 120 }}
                        />
                        <TextField
                            size="small" placeholder="Memo"
                            value={line.memo} onChange={e => updateLine(index, 'memo', e.target.value)}
                            InputProps={{ sx: { fontFamily, fontSize: '0.8rem' } }}
                            sx={{ flex: 1 }}
                        />
                        <IconButton size="small" onClick={() => removeLine(index)} disabled={lines.length <= 2} sx={{ color: '#94a3b8' }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}

                <Button size="small" startIcon={<AddIcon />} onClick={addLine}
                    sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                >
                    Add Line
                </Button>

                {/* Totals */}
                <Box sx={{
                    display: 'flex', gap: 1, mt: 2, p: 2, borderRadius: '12px',
                    bgcolor: isBalanced ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)',
                    border: `1px solid ${isBalanced ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                }}>
                    <Box sx={{ flex: 2 }} />
                    <Box sx={{ width: 120 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Total Debits</Typography>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>${formatCurrency(totalDebit)}</Typography>
                    </Box>
                    <Box sx={{ width: 120 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Total Credits</Typography>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>${formatCurrency(totalCredit)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Difference</Typography>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: isBalanced ? ACCENT : '#ef4444' }}>
                            {isBalanced ? 'Balanced' : `$${formatCurrency(difference)}`}
                        </Typography>
                    </Box>
                    <Box sx={{ width: 36 }} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button variant="outlined" onClick={() => handleSave(false)} disabled={saving}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', borderColor: ACCENT, color: ACCENT }}
                >
                    Save Draft
                </Button>
                <Button variant="contained" onClick={() => handleSave(true)} disabled={saving || !isBalanced}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                >
                    Post Entry
                </Button>
            </DialogActions>
        </Dialog>
    );
}
